import isNil from 'lodash/isNil';

import { MessageMedium } from 'constants/messageMediums';
import { DateType } from 'ui/datePicker';
import { getNewDate } from 'utils/date';
import { convertToPercent, convertToUnknownCurrency } from 'utils/convert';
import {
  CAMEL_CASED_CLASS_NODE_TYPE_MAP,
  MetricValueType,
  NODE_TYPES,
  SendMetric,
  TILE_METRICS_STUB,
} from '../../constants';
import type {
  Metric,
  MetricDefinition,
  MetricDefinitionsBySendAction,
  Workflow,
} from '../../types';

type CampaignId = string;

type Campaign = {
  id: number;
  messageMedium: MessageMedium;
  [key: string]: any;
};

interface SelectedMetric {
  /**
   * The name of the metric.
   * Corresponds to `MetricDefinition['metricName']`
   */
  name: string;
}

const getAllMetricTypes = (tileMetrics: MetricDefinitionsBySendAction = TILE_METRICS_STUB) => {
  return Object.values(tileMetrics).reduce(
    (acc: SelectedMetric[], metrics: MetricDefinition[] = []) => {
      /** This check is needed because of `_typename`` */
      if (!Array.isArray(metrics)) return acc;

      return [
        ...acc,
        ...(metrics.map((metric) => ({ name: metric.metricName })) as SelectedMetric[]),
      ];
    },
    [],
  );
};

export const getDefaultMetricsDateRange = (createdAt: string, timeZone?: string) => ({
  startDate: getNewDate({ date: createdAt, timeZone }),
  endDate: getNewDate({ timeZone }),
});

/**
 * Adds Embedded and WhatsApp Metrics defaults to the selected metrics.  This should be
 * removed when the backend is able to handle embedded and whatsApp within flex metrics.
 * @param selectedMetrics Current selected metrics
 * @returns Selected metrics with embedded or whatsApp metrics added
 */
export const addStubbedMetricsToSelectedMetrics = (
  selectedMetrics: MetricDefinitionsBySendAction = TILE_METRICS_STUB,
) => {
  const embeddedName = CAMEL_CASED_CLASS_NODE_TYPE_MAP[NODE_TYPES.SEND_EMBEDDED];
  const whatsAppName = CAMEL_CASED_CLASS_NODE_TYPE_MAP[NODE_TYPES.SEND_WHATSAPP];

  return {
    ...selectedMetrics,
    ...(!selectedMetrics[embeddedName] ? { [embeddedName]: TILE_METRICS_STUB[embeddedName] } : {}),
    ...(!selectedMetrics[whatsAppName] ? { [whatsAppName]: TILE_METRICS_STUB[whatsAppName] } : {}),
  };
};

export const getSendMetricsRequestData = ({
  campaigns = [],
  workflow,
  dateRange,
  selectedMetrics,
}: {
  campaigns: Campaign[];
  workflow: Pick<Workflow, 'createdAt' | 'id'>;
  dateRange?: {
    startDate?: DateType;
    endDate?: DateType;
  };
  selectedMetrics?: MetricDefinitionsBySendAction;
}) => {
  const campaignIds: number[] = [];
  const campaignsToMediums: Record<CampaignId, MessageMedium> = {};

  campaigns.forEach((campaign) => {
    const campaignId = campaign?.id;
    campaignIds.push(campaignId);
    campaignsToMediums[campaignId] = campaign?.messageMedium;
  });

  const startTime = (dateRange?.startDate || new Date(workflow.createdAt as string)).toISOString();
  const endTime = (dateRange?.endDate || new Date()).toISOString(); // up to present

  return {
    campaignIds,
    startTime,
    endTime,
    workflowId: workflow.id,
    // TODO: [WF-3572] only include metrics that are relevant to the campaigns
    selectedMetrics: getAllMetricTypes(addStubbedMetricsToSelectedMetrics(selectedMetrics)),
    campaignsToMediums, // used for sales/demo extension
  };
};

export const isMetricEmpty = (value: Metric['value']): value is undefined => isNil(value);

export const EMPTY_METRIC_VALUE = '--';

export const formatMetricNumber = (metric: Metric = {}) => {
  const { value, valueType } = metric;

  if (isMetricEmpty(value)) return EMPTY_METRIC_VALUE;
  // objects are not supported, hence the empty value
  if (typeof value === 'object') return EMPTY_METRIC_VALUE;

  switch (valueType) {
    case MetricValueType.Percentage:
      return convertToPercent(value, 2);
    case MetricValueType.Currency:
      return convertToUnknownCurrency(value);
    default:
      return typeof value === 'number' ? `${value.toLocaleString()}` : value;
  }
};

export const getMetricTitle = (metric: MetricDefinition) => {
  switch (metric.metricName) {
    case SendMetric.TotalEmailSend:
    case SendMetric.TotalPushSend:
    case SendMetric.TotalSMSSend:
    case SendMetric.TotalWebPushSend:
    case SendMetric.TotalInAppSend:
    case SendMetric.TotalWhatsAppSend:
      return 'Total sent';
    case SendMetric.EmailDeliveryRate:
    case SendMetric.PushDeliveryRate:
    case SendMetric.SMSDeliveryRate:
    case SendMetric.WhatsAppDeliveryRate:
      return 'Delivery rate';
    case SendMetric.EmailOpenRate:
    case SendMetric.PushOpenRate:
      return 'Open rate';
    case SendMetric.EmailClickRate:
    case SendMetric.EmbeddedClickRate:
    case SendMetric.WhatsAppClickRate:
      return 'Click rate';
    case SendMetric.TotalWebPushClick:
    case SendMetric.TotalInAppClick:
      return 'Total clicks';
    case SendMetric.TotalEmbeddedRetrieved:
      return 'Total retrieved';
    case SendMetric.EmbeddedImpressionsRate:
      return 'Impressions rate';
    default:
      return metric.metricNiceName;
  }
};
